import { useState, useCallback, useEffect } from 'react';
import { useLiveKit, LiveKitContextType, CallStatuses } from 'contexts/LiveKitContext';
import { useLogging } from 'contexts/LoggingContext';

/**
 * Return type for the useAgentCallConsole hook
 * Extends LiveKit context with additional local state and call control functions
 */
export interface UseAgentCallConsoleReturn {
    // LiveKit state (from context)
    room: LiveKitContextType['room'];
    callStatus: LiveKitContextType['callStatus'];
    transferTarget: LiveKitContextType['transferTarget'];
    activeCallData: LiveKitContextType['activeCallData'];
    agentAvailable: LiveKitContextType['agentAvailable'];
    callTranscript: LiveKitContextType['callTranscript'];
    callDetails: LiveKitContextType['callDetails'];
    availableStatuses: LiveKitContextType['availableStatuses'];
    clientInfo: LiveKitContextType['clientInfo'];
    partyInfo: LiveKitContextType['partyInfo'];
    qualifications: LiveKitContextType['qualifications'];
    aggregatedTagsUsed: LiveKitContextType['aggregatedTagsUsed'];
    aggregatedQualifications: LiveKitContextType['aggregatedQualifications'];
    isQualificationsLoading: LiveKitContextType['isQualificationsLoading'];

    // Local state (managed by this hook)
    isMuted: boolean;
    isOnHold: boolean;
    hasUserInteracted: boolean;
    showAudioPrompt: boolean;
    shouldCloseCallConsole: boolean;
    setShouldCloseCallConsole: (shouldClose: boolean) => void;

    // Call control functions
    handleMuteToggle: () => void;
    handleHoldToggle: () => void;
    handleToggle: () => void;
    handleAcceptCall: LiveKitContextType['acceptCall'];
    handleRejectCall: LiveKitContextType['rejectCall'];
    handleHangupCall: LiveKitContextType['hangUp'];
    handleMakeCall: LiveKitContextType['makeCall'];
    handleAgentAvailability: LiveKitContextType['updateAgentAvailability'];

    // Audio prompt control
    setShowAudioPrompt: (show: boolean) => void;
}

/**
 * Comprehensive custom hook for agent call console functionality
 * Handles audio setup, call status management, ringing sounds, and user interactions
 * Can be shared between different components with different markup
 */
export const useAgentCallConsole = (): UseAgentCallConsoleReturn => {
    const { info } = useLogging();
    // LiveKit context
    const {
        room,
        callStatus,
        transferTarget,
        mute: muteCall,
        unmute: unmuteCall,
        hangUp: hangUpCall,
        acceptCall,
        rejectCall,
        updateAgentAvailability,
        activeCallData,
        agentAvailable,
        callTranscript,
        callDetails,
        availableStatuses,
        clientInfo,
        partyInfo,
        qualifications,
        aggregatedTagsUsed,
        aggregatedQualifications,
        isQualificationsLoading,
        makeCall,
        shouldCloseCallConsole,
        setShouldCloseCallConsole,
    } = useLiveKit();

    // Call state management
    const [isMuted, setIsMuted] = useState(false);
    const [isOnHold, setIsOnHold] = useState(false);

    // Audio management
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const [showAudioPrompt, setShowAudioPrompt] = useState(false);

    // Set up interaction listeners (only called if auto-enable fails)
    const setupInteractionListeners = useCallback(() => {
        const handleInteraction = () => {
            if (!hasUserInteracted) {
                setHasUserInteracted(true);
                setShowAudioPrompt(false);
                console.log('User interaction detected, audio notifications enabled');
            }
        };

        // Listen for any user interaction
        document.addEventListener('click', handleInteraction, { once: true });
        document.addEventListener('keydown', handleInteraction, { once: true });
        document.addEventListener('touchstart', handleInteraction, { once: true });

        // Show prompt only if audio is needed but blocked
        const promptTimer = setTimeout(() => {
            if (!hasUserInteracted) {
                setShowAudioPrompt(true);
            }
        }, 2000); // Wait 1 second before showing prompt

        return () => {
            document.removeEventListener('click', handleInteraction);
            document.removeEventListener('keydown', handleInteraction);
            document.removeEventListener('touchstart', handleInteraction);
            clearTimeout(promptTimer);
        };
    }, [hasUserInteracted]);

    // Try to enable audio immediately on page load
    useEffect(() => {
        const enableAudioImmediately = async () => {
            try {
                // Try to create and test audio context immediately
                const audioContext = new window.AudioContext();
                await audioContext.resume();

                // If we get here, audio is allowed
                setHasUserInteracted(true);
                info(
                    'Audio enabled automatically',
                    { callStatus },
                    'useAgentCallConsole',
                    'audio_enabled_automatically'
                );
            } catch (error) {
                console.log('Browser requires user interaction for audio');
                // Set up interaction listeners only if auto-enable fails
                setupInteractionListeners();
            }
        };

        enableAudioImmediately();
    }, [setupInteractionListeners]);

    // Monitor LiveKit call status changes
    useEffect(() => {
        console.log('LiveKit call status changed:', callStatus);

        if (callStatus === CallStatuses.INCOMING && !hasUserInteracted) {
            // Show audio prompt if there's an incoming call but audio is blocked
            setShowAudioPrompt(true);
        } else if (callStatus !== CallStatuses.INCOMING) {
            // Hide audio prompt when call ends
            setShowAudioPrompt(false);
        }

        // When there's an incoming call, set client info
        if (callStatus === CallStatuses.READY || callStatus === CallStatuses.TRANSFERRED) {
            // Reset states when call ends
            setIsMuted(false);
            setIsOnHold(false);
        }
    }, [callStatus, hasUserInteracted]);

    // Call control functions
    const handleMuteToggle = useCallback(() => {
        console.log('Toggling mute state from:', isMuted);
        if (isMuted) {
            unmuteCall();
        } else {
            muteCall();
        }
        setIsMuted(!isMuted);
    }, [isMuted, muteCall, unmuteCall]);

    const handleHoldToggle = useCallback(() => {
        // Toggle hold state
        const newHoldState = !isOnHold;
        console.log('Toggling hold state to:', newHoldState);

        // Implement hold functionality - for now, just use mute
        if (newHoldState) {
            muteCall();
        } else {
            unmuteCall();
        }

        setIsOnHold(newHoldState);
    }, [isOnHold, muteCall, unmuteCall]);

    const handleToggle = useCallback(() => {
        const newStatus = !agentAvailable;
        console.log('Toggling agent availability to:', newStatus);
        // Using the context function to update availability
        updateAgentAvailability(newStatus);
        info(
            'Toggled agent availability',
            { isAgentAvailable: newStatus, callStatus },
            'useAgentCallConsole',
            'toggle_agent_availability'
        );
    }, [agentAvailable, updateAgentAvailability]);

    // Return all the state and functions needed by components
    return {
        // LiveKit state
        room,
        callStatus,
        transferTarget,
        activeCallData,
        agentAvailable,
        callTranscript,
        callDetails,
        availableStatuses,
        clientInfo,
        partyInfo,
        qualifications,
        aggregatedTagsUsed,
        aggregatedQualifications,
        isQualificationsLoading,

        // Local state
        isMuted,
        isOnHold,
        hasUserInteracted,
        showAudioPrompt,
        shouldCloseCallConsole,
        setShouldCloseCallConsole,

        // Call control functions
        handleMuteToggle,
        handleHoldToggle,
        handleToggle,
        handleAcceptCall: acceptCall,
        handleRejectCall: rejectCall,
        handleHangupCall: hangUpCall,
        handleMakeCall: makeCall,
        handleAgentAvailability: updateAgentAvailability,

        // Audio prompt control
        setShowAudioPrompt,
    };
};
